import { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface ExperiencePublic {
  id: string;
  company_name: string;
  title: string | null;
  title_progression: string | null;
  start_date: string | null;
  end_date: string | null;
  is_current: boolean | null;
  bullet_points: string[] | null;
  display_order: number | null;
}

function isExperiencePublic(value: unknown): value is ExperiencePublic {
  if (typeof value !== "object" || value === null) return false;
  const exp = value as Record<string, unknown>;
  return typeof exp.id === "string" &&
    typeof exp.company_name === "string" &&
    (typeof exp.title === "string" || exp.title === null) &&
    (typeof exp.title_progression === "string" || exp.title_progression === null) &&
    (typeof exp.start_date === "string" || exp.start_date === null) &&
    (typeof exp.end_date === "string" || exp.end_date === null) &&
    (typeof exp.is_current === "boolean" || exp.is_current === null) &&
    (Array.isArray(exp.bullet_points) || exp.bullet_points === null) &&
    (typeof exp.display_order === "number" || exp.display_order === null);
}

const ExperienceSection = () => {
  const [experiences, setExperiences] = useState<ExperiencePublic[]>([]);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/experiences")
      .then(r => r.json())
      .then((data: unknown) => {
        if (Array.isArray(data)) setExperiences(data.filter(isExperiencePublic));
      });
  }, []);

  if (experiences.length === 0) return null;

  return (
    <section id="experience" className="py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl font-serif font-bold text-foreground mb-3">
            Experience
          </h2>
          <p className="text-muted-foreground mb-12 max-w-2xl">
            Each role tells a story. Ask the AI for the deeper context behind any bullet point.
          </p>
        </motion.div>

        <div className="space-y-6">
          {experiences.map((exp, i) => {
            const dates = `${exp.start_date || "?"} – ${exp.is_current ? "Present" : exp.end_date || "?"}`;
            return (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-xl overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-xl font-serif font-semibold text-foreground">
                        {exp.company_name}
                      </h3>
                      <p className="text-primary text-sm font-medium">
                        {exp.title_progression || exp.title || ""}
                      </p>
                    </div>
                    <span className="text-sm text-muted-foreground">{dates}</span>
                  </div>

                  {(exp.bullet_points || []).length > 0 && (
                    <ul className="space-y-2 mt-4">
                      {(exp.bullet_points || []).map((a, j) => (
                        <li key={j} className="text-sm text-secondary-foreground flex items-start gap-2">
                          <span className="text-primary mt-1 shrink-0">▸</span>
                          {a}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
